import * as React from 'react';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import glamorous from 'glamorous';
import { RvLoader } from '../components/Loader';
import { SearchCriteria } from './SearchCriteria';
import { drawLabelsPlugin, formatNumber } from '../helpers/ChartHelper';
import CloseIcon from '@material-ui/icons/Close';
import { ChartCommonProps, IconButtonWrapper, ChartCommonState, HelpWrapper } from './Common';
import { WithStyles, withStyles, IconButton } from '@material-ui/core';
import { styles } from './styles';
import { ChartFilter } from './ChartComponents';
import { DrillDownType } from './DrillDownType';
import { getElementAtEvent, Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    ChartEvent,
    ActiveElement,
    Chart,
} from 'chart.js';
import { ChartInfo } from '../components/ChartInfo';
  
import { RV_CHART_CLASS, RV_CHART_COLORS, rvChartAnimation, rvTooltipStyle, rvLegendLabels, isChartDataEmpty } from './chartTheme';
import { ChartHeading } from './ChartHeading';
import { AnimatedNumber } from './AnimatedNumber';
import { ChartSkeleton } from '../components/loading/ChartSkeleton';
import { ChartEmptyState } from './ChartEmptyState';
import DonutLargeOutlinedIcon from '@material-ui/icons/DonutLargeOutlined';
ChartJS.register(ArcElement, Tooltip, Legend);
  
const BarWrapper = glamorous.div<{ theme?: object }>((props) => ({
    position: 'relative',
    paddingBottom: '10px',
    flex: 1,
    display: 'flex',
    flexFlow: 'column',
    maxWidth: '100%',
    maxHeight: '100%',
    height: '100%',
    '@media print': {
        display: 'flex',
        flexFlow: 'column',
        alignItems: 'center',
    }
}));

const BarTitleContainer = glamorous.div<{ theme?: object }>((props) => ({
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    padding: '15px 15px 0px 15px',
    fontSize: '16px',
    fontWeight: 600,
    [`& .${`h1`}`]: {

    },
    [`& .${`h2`}`]: {
        fontSize: '2.5rem',
        padding: '10px 0',
    },
    [`& .${`h3`}`]: {
        paddingTop: '10px'
    },
})).withProps({ className: 'rv-chart-title-area' });

const BarContainer = glamorous.div<{ theme?: object }>((props) => ({
    padding: '5px 15px 15px 15px',
    minHeight: '300px',
    flex: 1,
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: '350px',
    maxWidth: 'auto',
    position: 'relative',
    '@media (max-width: 533px)': {
        maxWidth: '350px',
        margin: '0 auto',
    },
    [`& .${`col`}`]: {
        flex: 1,
    }
}));

const LoadWrapper = glamorous.div<{ theme?: object }>((props) => ({
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
}));

const TotalDiv = glamorous.div<{ theme?: object }>((props) => ({
    cursor: 'pointer',
    paddingRight: 5
}));

export interface SelectedItemProps {
    getSelectedItem?: (
        searchCriterias: Array<SearchCriteria>, 
        isShowCompleted: boolean, 
        title?: string,
        drillDownType?: DrillDownType,
        defaultView?: string,
        alternativeKey?: {
            [key: string]: any
        },
        sortColumn?: string,
        sortDirection?: string,
        isIncludeNameFinancial?: boolean,
        isLead?: boolean,
    ) => void;
}
interface ChartProps extends  SelectedItemProps, ChartCommonProps, WithStyles<typeof styles> {
    chartFilter?: ChartFilter;
}

interface ChartState extends ChartCommonState {
    // `
}

class MattersByStatusChart extends React.Component<ChartProps, ChartState>  {

    // tslint:disable-next-line: no-any
    public chartRef: React.RefObject<any>;

    constructor(props: ChartProps, state: ChartState) {
        super(props, state);
        this.onCloseChart = this.onCloseChart.bind(this);
        this.handlePopoverOpen = this.handlePopoverOpen.bind(this);
        this.handlePopoverClose = this.handlePopoverClose.bind(this);
        this.handleTotalClick = this.handleTotalClick.bind(this);
        this.onHover = this.onHover.bind(this);
        this.chartRef = React.createRef();
        this.state = {
            open: false,
            anchorEl: undefined
        };
    }

    public componentDidMount() {
        // 
    }

    public componentWillUnmount() {
        // 
    }

    public shouldComponentUpdate(nextProps: ChartProps, nextState: ChartState): boolean {
        if (this.props !== nextProps || this.state !== nextState) {
           return true;
        }

        return false;
    }

    public render() {
        // const { classes } = this.props;

        return (
            <React.Fragment>
                {/* tslint:disable-next-line: no-any */}
                <Query<any> 
                    query={ChartData}
                    variables={{
                        entityType: this.props.chartFilter?.entityType,
                        entityGuid: this.props.chartFilter?.entityGuid,
                        responsibility: this.props.chartFilter?.responsibility,
                        filter: this.props.chartFilter?.filter
                    }}
                >
                    {({ loading, error, data }) => {

                        if (loading) {
                            return (
                                <ChartSkeleton variant="pie" />
                            );
                        }

                        const statistics = data.statistics;

                        let matter = (loading || error ||
                            statistics === null || statistics === undefined)
                            ? null
                            : statistics.matter;

                        let mattersByStatus = matter ? matter.mattersByStatus : null;

                        let matterByStatus = mattersByStatus ? mattersByStatus.matterByStatus : null;

                        let total = 0;

                        let statuses = matterByStatus ?
                            // tslint:disable-next-line: no-any
                            matterByStatus.map((value: any) => {
                                return value.status;
                            }) : null;

                        let counts = matterByStatus ?
                            // tslint:disable-next-line: no-any
                            matterByStatus.map((value: any) => {
                                total += value.count;
                                return value.count;
                            }) : null;

                        if (matterByStatus) {
                            return (
                                <BarWrapper className={RV_CHART_CLASS}>
                                    <IconButtonWrapper
                                        aria-label="close"
                                    >
                                        <HelpWrapper className="rv-chart-help">
                                            <ChartInfo 
                                                description={this.props.description}
                                            />
                                        </HelpWrapper>
                                        <IconButton
                                            aria-label="close"
                                            onClick={this.onCloseChart}
                                            size="small"
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </IconButtonWrapper>
                                    <BarTitleContainer>
                                        <ChartHeading icon={<DonutLargeOutlinedIcon />} title="Current Matters" />
                                        <TotalDiv className="h2" onClick={() => this.handleTotalClick()}>
                                            <AnimatedNumber value={total} format={(amount) => formatNumber(amount)} />
                                        </TotalDiv>
                                        <div className="h3 rv-chart-section">Matters by Status</div>
                                    </BarTitleContainer>
                                    <BarContainer className="rv-chart-bars">
                                        <>
                                        <Pie
                                            ref={this.chartRef}
                                            // tslint:disable-next-line: no-any
                                            onClick={(event: React.MouseEvent<any>) => {
                                                const _data = getElementAtEvent(this.chartRef.current, event);
                                                if (_data.length > 0 && this.props.getSelectedItem) {

                                                    const _index = getElementAtEvent(this.chartRef.current, event)[0].index;

                                                    const item = statuses[_index];

                                                    const searchCriterias = new Array<SearchCriteria>();

                                                    searchCriterias.push({
                                                        property: 'Status',
                                                        operator: 'eq',
                                                        comparison: item
                                                    });

                                                    this.props.getSelectedItem(
                                                        searchCriterias,
                                                        false,
                                                        'Matters by Status',
                                                        DrillDownType.matterSummaryDrilldown,
                                                        'Matters',
                                                    );
                                                }
                                            }}
                                            data={{
                                                labels: statuses,
                                                datasets: [{
                                                    data: counts,
                                                    backgroundColor: [
                                                        RV_CHART_COLORS.red,
                                                        RV_CHART_COLORS.primary,
                                                        RV_CHART_COLORS.amber,
                                                        RV_CHART_COLORS.green,
                                                        RV_CHART_COLORS.primarySoft,
                                                        '#8b5cf6',
                                                    ],
                                                    hoverBackgroundColor: [
                                                        RV_CHART_COLORS.red,
                                                        RV_CHART_COLORS.primary,
                                                        RV_CHART_COLORS.amber,
                                                        RV_CHART_COLORS.green,
                                                        RV_CHART_COLORS.primarySoft,
                                                        '#8b5cf6',
                                                    ],
                                                    borderColor: '#ffffff',
                                                    borderWidth: 2,
                                                    hoverOffset: 8,
                                                }]
                                            }}
                                            options={{
                                                onHover: this.onHover,
                                                animation: { ...rvChartAnimation(), animateRotate: true, animateScale: true },
                                                layout: { padding: 8 },
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                  legend: {
                                                    position: 'bottom' as const,
                                                    display: true,
                                                    labels: rvLegendLabels,
                                                  },
                                                  title: {
                                                    display: false,
                                                    text: 'Matters by Status',
                                                  },
                                                  tooltip: {
                                                    ...rvTooltipStyle,
                                                    callbacks: {
                                                        // tslint:disable-next-line: no-any
                                                        label: function (context: any) {
                                                            const { 
                                                            // dataIndex, 
                                                            label, 
                                                            formattedValue 
                                                            } = context;
                                                            // tslint:disable-next-line: radix
                                                            return `${label} ${formattedValue}`;
                                                        }
                                                    }
                                                  }
                                                },
                                            }}
                                            plugins={[drawLabelsPlugin]}
                                        />
                                        {isChartDataEmpty(counts) && (
                                            <ChartEmptyState title="No matters" hint="There are no current matters." />
                                        )}
                                        </>
                                    </BarContainer>
                                </BarWrapper>
                            );
                        }

                        return (
                            <LoadWrapper className="load-wrapper">
                                <RvLoader size="small" />
                            </LoadWrapper>
                        );
                    }}
                </Query>
            </React.Fragment>
        );
    }

    private handleTotalClick() {
        if (this.props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            searchCriterias.push(
                { property: 'InProgress', operator: 'eq', comparison: '1'}
            );

            this.props.getSelectedItem(
                searchCriterias,
                false,
                'Matters by Status',
                DrillDownType.matterSummaryDrilldown,
                'Matters'
            );
        }        
    }

    private onCloseChart() {
        if (this.props.onCloseChart) {
            return this.props.onCloseChart('isMattersByStatusChartOpen');
        }
    }

    // tslint:disable-next-line: no-any
    private handlePopoverOpen(event: any) {
        this.setAnchorEl(event.currentTarget);
    }

    private handlePopoverClose = () => {
        this.setAnchorEl(null);
    }

    // tslint:disable-next-line: no-any
    private setAnchorEl(anchorElValue: any) {
        this.setState({
            anchorEl: anchorElValue,
            open: Boolean(anchorElValue)
        });
    }
    
    private onHover(event: ChartEvent, elements: ActiveElement[], chart: Chart) {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    }
}

// tslint:disable-next-line: no-any
export default withStyles(styles as any, { withTheme: true })(MattersByStatusChart as any) as any;

const ChartData = gql`
    query MattersByStatusChartData ($entityType: Int, $entityGuid: String, $responsibility: Int, $filter: String) {
        statistics{
            matter{
                mattersByStatus (entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility, filter: $filter) {
                    matterByStatus{
                        status,
                        count
                    }
                }
            }
        }
    }
`;
